'use strict';

/**
 * Sort of like a base class for all kinds of rest clients.  Until we move to something like TypeScript
 * (which has real inheritance), use this by constructing a new base element and invoking functions on it.
 *
 * e.g.
 *
 * var base = new HttpClientBase(endpoint);
 * base.url('foo/{}/bar', 16, {page: 1})
 */
customerZone.factory('RestClientBase', ['$log', 'errorCode',
    function($log, errorCode){

        /** constructor function
         * @param endpoint endpoint url of the rest service to use
         * @constructor
         */
        var RestClientBase = function(endpoint) {

            if (!(/\/$/.exec(endpoint))) endpoint = endpoint+'/';

            var self = this;

            var formatUrl = function(version, format, queryParams, path) {
                var result = endpoint;
                var lastPosition = 0;
                var pathIndex = 0;
                var pattern = /\{}/g;
                var match = pattern.exec(format);

                if (version)
                    result = result.replace(/(\w)\/(?!\/)/, '$1/'+version+'/');

                while (match) {
                    var argument = path[(pathIndex++)];
                    if (argument === undefined)
                        throw new Error('Attempt to use undefined as a url parameter.  URL: ' + format);
                    if ((''+argument).indexOf('/') >= 0)
                        throw new Error('Attempt to divert to another path.  URL: ' + format);
                    result += format.substr(lastPosition, pattern.lastIndex - lastPosition - 2);
                    result += encodeURIComponent(argument);
                    lastPosition = pattern.lastIndex;
                    match = pattern.exec(format);
                }

                result += format.substr(lastPosition);

                var firstParam = true;
                for (var key in queryParams) {
                    if (queryParams.hasOwnProperty(key)) {
                        var value = queryParams[key];
                        if (value !== undefined) {
                            result += firstParam ? '?' : '&';
                            result += key + '=' + encodeURIComponent(value);
                            firstParam = false;
                        }
                    }
                }
                return result;
            };

            /**
             * Url formatter for rest services.  The input is a string with curly braces as placeholders,
             * and a set of arguments.  The output is the full url, prefixed by the baseurl,
             * with all parameters filled in.  If this first argument is an object, it will be added
             * as query parameters.
             */
            self.url = function (format) {
                var argumentIndex = 1; // first argument is 'this'

                var queryParams = {};
                if (arguments.length > argumentIndex && typeof arguments[argumentIndex] === 'object')
                    queryParams = arguments[argumentIndex++];

                return formatUrl(undefined, format, queryParams, [].slice.call(arguments, [argumentIndex]));
            };

            self.versionedUrl = function(version, format) {
                var argumentIndex = 2; // first argument is 'this'

                var queryParams = {};
                if (arguments.length > argumentIndex && typeof arguments[argumentIndex] === 'object')
                    queryParams = arguments[argumentIndex++];

                return formatUrl(version, format, queryParams, [].slice.call(arguments, [argumentIndex]));
            };

            /**
             * Adapt the given $http config object to POST in Form URL-encoded format.
             * The $config object is returned for easy chaining
             */
            self.asUrlFormEncoded = function(config){
                config = angular.copy(config);
                if (!config.headers) config.headers = {};
                config.headers['Content-Type'] = 'application/x-www-form-urlencoded';
                config.transformRequest = function(data) {
                    var str = [];
                    for (var field in data)
                        str.push(encodeURIComponent(field) + '=' + encodeURIComponent(data[field]));
                    return str.join('&');
                };

                return config;
            };

            /**
             * Parse a date in iso format as moment
             */
            self.parseDate = function(isoDateString) {
                if (!isoDateString) return null;
                return moment.utc(isoDateString, 'YYYY-MM-DD');
            };

            self.formatDate = function(aMoment) {
                if (!aMoment) return undefined;
                return aMoment.format('YYYY-MM-DD');
            };

            self.formatDateIfNeeded = function(filter){
                if (filter instanceof moment){
                    return moment(filter, 'DD/MM/YYYY').format('YYYY-MM-DD');
                }
                else if (filter instanceof Date){
                    $log.debug('Date parameter found in model -- this is not supposed to happen.');
                    return moment(filter).format('YYYY-MM-DD');
                }
                else {
                    return filter;
                }
            };

            /**
             * Parse a date-time in iso format as moment
             */
            self.parseDateTime = function(isoDateString) {
                if (!isoDateString) return null;
                return moment.utc(isoDateString, 'YYYY-MM-DDThh:mm:ssZ');
            };

            /**
             * Generic promise handler that returns the response data as is.
             */
            self.unwrapResponse = function(response){
                return response.data;
            };

            /**
             * Unwrap given error and map fields if the error contains some using the given mapping.
             */
            self.unwrapError = function(response, mapping){
                if (response.status === -401)
                    throw errorCode.wrap(errorCode.SENT_TO_LOGIN);
                else if (response.status <= 0 || response.status === 408)
                    throw errorCode.wrap(errorCode.HTTP_CONNECTION_ERROR);
                else if (response.status === 429) {
                    throw errorCode.wrap(errorCode.REQUEST_THROTTLED, response.data);
                }
                else if (response.status === 401) {
                    throw errorCode.wrap(errorCode.INVALID_GRANT);
                }
                else if (Math.floor(response.status/100) === 4) {
                    if (response.headers('content-type') === 'application/json')
                        throw self.mapFields(response.data, mapping);
                    else
                        throw errorCode.wrap(errorCode.CLIENT_ERROR, response.data);
                }
                else {
                    throw errorCode.wrap(errorCode.SERVER_ERROR, response.data);
                }
            };

            /**
             * If the error contains fields, adapt their names according to the given mapping
             */
            self.mapFields = function(error, mapping){
                if (!error || !error['incorrect_fields']) return error;
                if (!mapping) mapping = {};
                error.incorrectFields = error['incorrect_fields'].map(function(issue){
                    issue.field = mapping[issue.field] || issue.field;
                    issue.errors = issue.errors.map(function(error){
                        // UWA has two almost the same ways of saying an email address is in use.  We just want one.
                        // see UWA-1698
                        if (error.code === 'email_address_already_in_use')
                            error.code = errorCode.EMAIL_ADDRESS_ALREADY_IN_USE;
                        return error;
                    });
                    return issue;
                });
                return error;
            };

            self.doNothing = function(){};

            /**
             * Generic promise failure handler that considers a 404 to be 'no entry found' and return null.
             * Other responses will be considered as a technical error.
             */
            self.interpretNotFoundAsNull = function(response){
                if (response.status === 404)
                    return null;
                else
                    throw self.unwrapError(response);
            };

            /**
             * Generic promise failure handler that considers a 404 to be 'no entries found' and return an empty list.
             * Other responses will be considered as a technical error.
             */
            self.interpretNotFoundAsEmpty = function(response){
                if (response.status === 404)
                    return [];
                else throw self.unwrapError(response);
            };


            /**
             * Recursively convert snake_case to camelCase in a javascript object.
             * If the given payload is an array, it will convert case for every element.
             * If the given payload is neither an object or array, it is simply returned.
             */
            self.snakeToCamel = function(payload){
                if (typeof payload !== 'object') return payload;
                if (payload === null) return null;
                if (Array.isArray(payload)) return payload.map(self.snakeToCamel);
                var keys = Object.keys(payload);
                var result = {};
                for (var i=0; i<keys.length; i++){
                    var key = keys[i];
                    var camelKey = key.replace(/(_\w)/g, function(m){ return m[1].toUpperCase(); });
                    result[camelKey] = self.snakeToCamel(payload[key]);
                }
                return result;
            };

            /**
             * Remove undefined properties from an object
             */
            self.removeUndefined = function(object){
                for (var key in object){
                    if (object[key] === undefined) {
                        delete object[key];
                    }
                }
                return object;
            };
        };

        return RestClientBase;
    }]);
